/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement, svg } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { baseFontSize } from '../../utils/base-font-size.js';

/**
 * @prop {String} label - Defines the label or heading of the risk score, shown on the left top of the linear variation and on top of the radial variation.
 * @prop {String} value - Represents the Score in percentge or number.
 * @prop {Number} segments - Defines the number of segments in the Riskscore
 * @prop {String} size - Defines the size of the radial Riskscore. Default is 's'.It's not applicable for linear variation.
 * @prop {Array} segmentValue - Defines the value of each segment in the Riskscore
 * @prop {Array} segmentColor - Defines the color for each segments in the Riskscore
 * @prop {Boolean} radial - If set , Radial riskscore is displayed
 * @prop {Boolean} disabled - If set , Riskscore is displayed as disabled.
 * @slot footer - Takes icons, text or links as children.
 */
export class SHRiskscore extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: String, reflect: true },
      segments: { type: Number, reflect: true },
      size: { type: String, reflect: true },
      segmentValue: { type: Array, reflect: true, attribute: 'segment-value' },
      segmentColor: { type: Array, reflect: true, attribute: 'segment-color' },
      radial: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      _currentAngle: { type: Number, reflect: false },
      _emptyFooter: { type: Boolean, reflect: true, attribute: 'empty-footer' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
            width: 100%;
            display: block;
        }
        .linear-wrapper{
            display: flex;
            height: 0.25rem;
            margin-bottom: 0.5rem;
            width: 100%;
            min-width: 7rem;
        }
        .footer-wrapper {
            display: flex;
            align-items: center;
            margin: 0rem 0rem 0.5rem 0rem;
          }
        .riskbar {
            margin-left: 0.25rem;
            width: 100%;
            background-color: rgba(var(--ui-1), var(--opacity-5));
            overflow: hidden;
            position: relative;
        }
        .value {
            margin-left: 0.25rem;
        } 
        .riskbar:first-child {
          margin-left: 0rem;
        }
        .header-wrapper {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        :host(:not([radial])) .label {    
          flex: 1;
          width: auto;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .label,
        .value {
          line-height: 1.25rem;
        }
        .value {         
          width: auto;
          text-align: right;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        :host([disabled]) .value {
          color: var(--text-disabled);
        }
        :host([disabled]) .riskbar ,
        :host([disabled]) .bar-value {
          background-color: rgba(var(--ui-1), var(--opacity-6));
        }
        :host([disabled]) .radial-segment , 
        :host([disabled]) .radial-background {
          stroke: rgba(var(--ui-1), var(--opacity-6));
        }
        .label + .value {
          margin-left: 0.5rem;
        }
        :host(:not([label])) .value {
          flex: 1;
          text-align: center;
        } 
        .bar-value {
          height: 0.25rem;
          position:absolute;
          max-width: 100%;
        }
        .footer {
          height: fit-content;
        }
        :host([empty-footer]) .footer-wrapper {
          display: none;
        }

        /*radial*/ 
        :host([radial]) {
          text-align: center;    
          width: 12rem; 
        }
        :host([radial]) .label {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        :host([disabled][radial]) .radial-label {
          color: var(--text-disabled);
        }
        .radial-wrapper {
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .radial {
          transform: rotate(-90deg);
          overflow: visible;
        }
        .radial-label {
          position: absolute;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          word-break: break-all;
          -webkit-box-orient: vertical;
          max-height: 4rem;
          width: 2.5rem;
        }
        :host([size='s']) .radial-background ,
        :host([size='s']) .radial-segment {
          stroke-width: 0.25rem;
        }
        :host([size='l']) .radial-background ,
        :host([size='l']) .radial-segment {
          stroke-width: 0.5rem;
        }
        :host([radial][size='s']) .radial-label {
          width: 2.5rem;
          line-height: 1rem;
        }
        :host([radial][size='l']) .radial-label {
          font-size: 1.75rem;
          width: 5.625rem;
          line-height: 2rem;
        }
        :host([radial]) .header-wrapper{
          margin-bottom: 0.5rem;
        }
        :host([radial]) .footer-wrapper {
          display: block;
          margin: 0rem auto 0.5rem auto;

        }
        :
      `,
    ];
  }

  render() {
    return html`
      <div class="risk-wrapper" empty-footer=${this._emptyFooter}>
        <div class="header-wrapper">
          <sh-text size="title-1" class="label">${this.label}</sh-text>
          ${this.value && !this.radial
            ? html` <sh-text size="title-1" class="value">${this.value}</sh-text> `
            : ''}
        </div>
        ${!this.radial
          ? html` <div class="linear-wrapper">${this._createLinearSegments()}</div> `
          : html`
              <div class="radial-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="radial"
                  viewBox="${this.size === 's' ? 0 : -2} 
                  ${this.size === 's' ? 0 : -2} 
                  ${this._convertRadialSize() + (this.size === 's' ? 0 : 4)} 
                  ${this._convertRadialSize() + (this.size === 's' ? 0 : 4)}"
                  preserveAspectRatio="xMidYMid meet"
                  style="width: ${this._convertRadialSize() /
                  baseFontSize}rem; height: ${this._convertRadialSize() / baseFontSize}rem"
                >
                  ${this._createRadialSegments()}
                </svg>
                ${this.value
                  ? html` <sh-text size="title-1" class="radial-label">${this.value}</sh-text> `
                  : ''}
              </div>
            `}
        <div class="footer-wrapper">
          <slot
            class="footer"
            id="footer"
            name="footer"
            @slotchange="${() => this._footerSlotObserver()}"
          ></slot>
        </div>
      </div>
    `;
  }

  constructor() {
    super();
    this.size = 's';
    this.segments = 1;
    this.label = 'Label';
    this.segmentColor = !this.segmentColor ? ['default'] : this.segmentColor;
    this.segmentValue = !this.segmentValue ? [''] : this.segmentValue;
    this._currentAngle = 90;
  }

  updated(changedProperties) {
    const listOfProperties = ['value'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
    this._footerSlotObserver();
  }

  _createLinearSegments() {
    let linearSegmentsArray = [];
    for (let segmentNo = 0; segmentNo < Math.round(this.segments); segmentNo++) {
      linearSegmentsArray.push(html`
        <div class="riskbar" style="width: 100%">
          <div
            class="bar-value"
            style="width: ${this._getSegmentValue(segmentNo)}% ;
        background-color: ${this._getSegmentColor(segmentNo)}"
          ></div>
        </div>
      `);
    }
    return linearSegmentsArray;
  }

  _createRadialSegments() {
    const radialSegmentsArray = [];
    for (let segmentNo = 0; segmentNo < Math.round(this.segments); segmentNo++) {
      radialSegmentsArray.push(svg`
                    <path class="radial-background" 
                      fill=none
                      stroke= 'rgba(var(--ui-1), var(--opacity-5))'
                      d= "${
                        this._calculateRadialSegmentPath(
                          segmentNo,
                          this._convertRadialSize(),
                          true
                        ) +
                        ' ' +
                        (this.segments === 1 ? 'Z' : '')
                      }"/>
                    
                    <path
                      class="radial-segment" 
                      fill=none 
                      stroke="${this._getSegmentColor(segmentNo)}"
                      d = "${
                        this._calculateRadialSegmentPath(
                          segmentNo,
                          this._convertRadialSize(),
                          false
                        ) +
                        ' ' +
                        (this.segments === 1 && this._getSegmentValue(0) === 100 ? 'Z' : '')
                      }"
                     />
                  `);
    }
    return radialSegmentsArray;
  }

  _getSegmentColor(segNo) {
    const disabledColor = 'rgba(var(--ui-1), var(--opacity-6))';
    this._calculateSegmentContent(segNo);
    let segmentColor = this.disabled ? disabledColor : this.segmentColor[segNo];
    return segmentColor;
  }

  _getSegmentValue(segNo) {
    this._calculateSegmentContent(segNo);
    let segmentValue = this.disabled ? 0 : this.segmentValue[segNo];
    return segmentValue;
  }

  _calculateSegmentContent(segNo) {
    const defaultColor = 'rgba(var(--ui-1), var(--opacity-5))';
    const activeColor = 'rgba(var(--ui-1), var(--opacity-3))';

    let segColor = this.segmentColor[segNo];
    let segValue = this.segmentValue[segNo];
    //default color or undefined will have value 0 and default color

    if (
      (!segColor || segColor === 'default' || segColor === defaultColor) &&
      (!segValue || segValue === 0)
    ) {
      this.segmentColor[segNo] = defaultColor;
      this.segmentValue[segNo] = 0;
    }

    //if value is greater than 0 & color is not defined  => segment takes active color
    else if (
      (!segColor || segColor === 'default' || segColor === 'active' || segColor === defaultColor) &&
      segValue > 0
    ) {
      this.segmentColor[segNo] = activeColor;
      if (!segValue && segValue !== 0) {
        this.segmentValue[segNo] = 100;
      }
    }

    //segment takes the user defiend color and value
    else {
      if (!segValue && segValue !== 0) {
        this.segmentValue[segNo] = 100;
      }
    }
  }

  _calculateRadialSegmentPath(segNo, radialsize, isOuter) {
    let cx, cy;
    cx = cy = radialsize / 2;
    let radius = radialsize / 2 - 4;
    const segmentGap = 8;
    let startAngle = this._currentAngle + 4;
    let segmentValue = this._getSegmentValue(segNo);
    let arcAngle = 360 / Math.round(this.segments) - segmentGap;
    let endAngle;
    if (isOuter) {
      endAngle = startAngle + arcAngle;
    } else {
      endAngle = segmentValue === 0 ? startAngle : startAngle + arcAngle / (100 / segmentValue);
      this._currentAngle += 360 / Math.round(this.segments);
    }

    return this._getArcPath(cx, cy, radius, startAngle, endAngle);
  }

  _polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    let angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }

  _getArcPath(x, y, radius, startAngle, endAngle) {
    let start = this._polarToCartesian(x, y, radius, endAngle);
    let end = this._polarToCartesian(x, y, radius, startAngle);

    let largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    let d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(
      ' '
    );

    return d;
  }

  _footerSlotObserver() {
    const footerNodes = this.shadowRoot.querySelector('#footer').assignedNodes({
      flatten: true,
    }).length;
    this._emptyFooter = footerNodes === 0;
  }

  _convertRadialSize() {
    let size;
    switch (this.size) {
      case 's':
        size = 64;
        break;
      case 'l':
        size = 128;
        break;
    }
    return size;
  }
}

customElements.define('sh-risk-score', SHRiskscore);
